import React from 'react';
import { Box, Card, Grid, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  EditableInput,
  PassFailChip
} from '@features/calibration/components/shared';
import { formatValueWithResolution } from '@features/calibration/components/shared/utils';
import { TestType } from '@features/calibration/types';
import type { BasicReadingTestPoint } from '@features/calibration/hooks/useCalibrationRuntime/types';
import { calculateError, inferResolution } from '../utils';
import type { CommitMeasurementHandler, MeasurementSide } from '../types';
import { MeasurementImageAction } from './MeasurementImageCell';

type RunnerTabletCardProps = {
  point: BasicReadingTestPoint;
  unit: string;
  showAsFound: boolean;
  showAsLeft: boolean;
  readOnly?: boolean;
  isCurrentPoint?: boolean;
  landedSide?: MeasurementSide | null;
  landedAt?: number | null;
  runId?: string;
  compact?: boolean;
  onCommitMeasurement?: CommitMeasurementHandler;
};

const SIDE_TINT: Record<MeasurementSide, string> = {
  asFound: 'rgba(33, 150, 243, 0.03)',
  asLeft: 'rgba(76, 175, 80, 0.03)'
};

const RunnerTabletCard: React.FC<RunnerTabletCardProps> = ({
  point,
  unit,
  showAsFound,
  showAsLeft,
  readOnly = false,
  isCurrentPoint = false,
  landedSide = null,
  landedAt = null,
  runId,
  compact = false,
  onCommitMeasurement
}) => {
  const { t } = useTranslation('calibration');
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const resolution = inferResolution(point);
  const readability = resolution > 0 ? Math.pow(10, -resolution) : 1;
  const pointUnit = point.unit ?? unit;

  const renderSide = (side: MeasurementSide) => {
    const value = side === 'asFound' ? point.asFound : point.asLeft;
    const overridden =
      side === 'asFound' ? point.asFoundOverridden : point.asLeftOverridden;
    const imageId =
      side === 'asFound' ? point.asFoundImageId : point.asLeftImageId;
    const error = calculateError(value, point.nominalValue);
    const justLanded = landedSide === side && landedAt !== null;

    const isReceivingSide =
      isCurrentPoint &&
      value === null &&
      (side === 'asFound' || point.asFound !== null);

    return (
      <Box
        key={justLanded ? `${side}-${landedAt}` : side}
        sx={{
          p: 1,
          borderRadius: 1,
          backgroundColor: isDarkMode ? 'transparent' : SIDE_TINT[side],
          ...(justLanded && {
            '@keyframes runnerPulse': {
              from: { backgroundColor: 'rgba(249, 168, 37, 0.45)' },
              to: { backgroundColor: 'transparent' }
            },
            animation: 'runnerPulse 1.2s ease-out'
          })
        }}
      >
        <Typography variant="caption" color="text.secondary" display="block">
          {t(side === 'asFound' ? 'testGrid.asFound' : 'testGrid.asLeft')}
        </Typography>
        {isReceivingSide ? (
          <Box
            sx={{
              display: 'inline-block',
              border: '2px solid #f9a825',
              borderRadius: 2,
              px: 1.5,
              py: 0.5,
              color: isDarkMode ? 'rgba(251, 191, 36, 1)' : '#9a6700'
            }}
          >
            <Typography variant="body2">{t('runner.reading')}</Typography>
          </Box>
        ) : readOnly ? (
          <Typography variant="body2" fontWeight={600}>
            {value !== null
              ? `${formatValueWithResolution(value, resolution)} ${pointUnit}`
              : '--'}
          </Typography>
        ) : (
          <Box
            sx={{
              ...(overridden && {
                border: '2px solid #f9a825',
                borderRadius: 2,
                backgroundColor: isDarkMode
                  ? 'rgba(249, 168, 37, 0.08)'
                  : '#fffdf5',
                p: 0.25
              })
            }}
          >
            <EditableInput
              field={side}
              value={value}
              point={{
                id: point.id,
                nominal: point.nominalValue,
                asFound: point.asFound,
                asLeft: point.asLeft
              }}
              readability={readability}
              resolution={resolution}
              unit={pointUnit}
              onUpdate={(field, updateValue) =>
                onCommitMeasurement?.(
                  point.id,
                  field as MeasurementSide,
                  updateValue === null || updateValue === ''
                    ? null
                    : Number(updateValue)
                )
              }
              testType={TestType.BasicReading}
              isDisabled={!onCommitMeasurement}
            />
          </Box>
        )}
        {overridden && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.25,
              color: isDarkMode ? 'rgba(251, 191, 36, 1)' : '#9a6700'
            }}
          >
            ✎ operator override
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
          <Typography variant="caption" color="text.secondary">
            {t('testGrid.error')}:
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight:
                error !== null && Math.abs(error) > point.tolerance ? 600 : 400,
              color:
                error !== null && Math.abs(error) > point.tolerance
                  ? 'error.main'
                  : 'inherit'
            }}
          >
            {error !== null
              ? formatValueWithResolution(error, resolution)
              : '--'}
          </Typography>
          <PassFailChip error={error} tolerance={point.tolerance} />
          <MeasurementImageAction imageId={imageId} runId={runId} />
        </Box>
      </Box>
    );
  };

  const sideColumns = compact ? 12 : 6;

  return (
    <Card
      sx={{
        p: 1.5,
        mb: 1,
        border: '1px solid',
        borderColor: isCurrentPoint ? '#f9a825' : 'divider',
        ...(isCurrentPoint && {
          backgroundColor: isDarkMode ? 'rgba(249, 168, 37, 0.08)' : '#fffbea'
        })
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          mb: 1
        }}
      >
        <Typography variant="body2" fontWeight={700}>
          {point.label ??
            `${formatValueWithResolution(point.nominalValue, resolution)} ${pointUnit}`}
        </Typography>
        <Typography variant="body2" fontWeight={700}>
          ±{formatValueWithResolution(point.tolerance, resolution)} {pointUnit}
        </Typography>
      </Box>
      <Grid container spacing={1.5}>
        {showAsFound && (
          <Grid item xs={sideColumns}>
            {renderSide('asFound')}
          </Grid>
        )}
        {showAsLeft && (
          <Grid item xs={sideColumns}>
            {renderSide('asLeft')}
          </Grid>
        )}
      </Grid>
    </Card>
  );
};

export default RunnerTabletCard;
